/**
 * iter-170ai — Public read-only view for accountant links.
 *
 * Mounted at ``/r/:token`` (no auth required). Renders a curated
 * snapshot of the user's data: cash accounts, debts, last 500
 * movements. No PII (email, payment methods, profile lang/name only).
 *
 * The page intentionally keeps a stripped-down chrome (no sidebar,
 * no global header) so it's obvious to the visitor that they're
 * in a shared view and not the owner's account.
 */
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { Card } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Eye, AlertCircle, Lock } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function fmtMoney(amount, currency) {
  try {
    return new Intl.NumberFormat("es-CO", {
      style: "currency", currency: currency || "USD",
      maximumFractionDigits: 0,
    }).format(amount || 0);
  } catch { return `${amount} ${currency || ""}`; }
}

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("es-CO",
      { year: "numeric", month: "short", day: "2-digit" });
  } catch { return iso; }
}

export default function ReadOnlyView() {
  const { token } = useParams();
  const { data, isLoading, error } = useQuery({
    queryKey: ["readonly-snapshot", token],
    queryFn: async () => (await axios.get(`${API}/api/r/${token}/summary`)).data,
    retry: false,
  });

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <p className="text-sm text-slate-500">Cargando…</p>
      </div>
    );
  }

  if (error || !data) {
    const status = error?.response?.status;
    const isExpired = status === 410;
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <Card className="p-8 max-w-md text-center" data-testid="readonly-error">
          <div className="h-12 w-12 rounded-full bg-rose-100 text-rose-700 mx-auto mb-3 flex items-center justify-center">
            {isExpired ? <Lock className="h-5 w-5" /> : <AlertCircle className="h-5 w-5" />}
          </div>
          <h1 className="font-display text-xl font-bold text-slate-900 mb-2">
            {isExpired ? "Link expirado" : "Link inválido o revocado"}
          </h1>
          <p className="text-sm text-slate-600">
            {isExpired
              ? "Este link de acceso ya no es válido. Pide a tu cliente uno nuevo."
              : "Este link no existe, fue revocado o expiró. Pide a tu cliente uno nuevo."}
          </p>
        </Card>
      </div>
    );
  }

  const { label, owner, movements, debts, cash_accounts } = data;
  const ccy = owner?.currency || "USD";

  return (
    <div className="min-h-screen bg-slate-50" data-testid="readonly-view">
      {/* Banner — make it impossible to mistake this for the editor view */}
      <div className="bg-sky-600 text-white px-4 py-2.5">
        <div className="max-w-5xl mx-auto flex items-center gap-2 text-sm">
          <Eye className="h-4 w-4" />
          <span className="font-semibold">Vista solo-lectura</span>
          <span className="opacity-80">·</span>
          <span className="opacity-90">{label}</span>
        </div>
      </div>

      <div className="max-w-5xl mx-auto p-6 space-y-6">
        {/* Cash accounts */}
        <Card className="p-5 rounded-2xl" data-testid="readonly-accounts">
          <h2 className="font-semibold text-slate-900 mb-3">Cuentas</h2>
          {cash_accounts.length === 0 ? (
            <p className="text-xs text-slate-500 italic">Sin cuentas registradas.</p>
          ) : (
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {cash_accounts.map((a) => (
                <li key={a.id} className="p-3 rounded-xl border border-slate-200 bg-white flex items-center justify-between">
                  <div>
                    <div className="text-sm font-medium text-slate-900">{a.name}</div>
                    <div className="text-[11px] text-slate-500 uppercase">{a.kind || "—"}</div>
                  </div>
                  <div className="text-sm font-mono tabular-nums">
                    {fmtMoney(a.balance, a.currency)}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>

        {/* Debts */}
        <Card className="p-5 rounded-2xl" data-testid="readonly-debts">
          <h2 className="font-semibold text-slate-900 mb-3">Deudas</h2>
          {debts.length === 0 ? (
            <p className="text-xs text-slate-500 italic">Sin deudas registradas.</p>
          ) : (
            <ul className="space-y-2">
              {debts.map((d) => (
                <li key={d.id} className="p-3 rounded-xl border border-slate-200 bg-white">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-slate-900">{d.name}</span>
                    <span className="text-sm font-mono tabular-nums text-rose-700">
                      {fmtMoney(d.balance, d.currency)}
                    </span>
                  </div>
                  <div className="text-[11px] text-slate-500 flex flex-wrap gap-3">
                    {d.kind && <span>Tipo: <Badge variant="outline" className="text-[10px]">{d.kind}</Badge></span>}
                    {d.interest_rate != null && <span>Tasa: {d.interest_rate}%</span>}
                    {d.monthly_payment != null && <span>Cuota: {fmtMoney(d.monthly_payment, d.currency)}</span>}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>

        {/* Movements */}
        <Card className="p-5 rounded-2xl" data-testid="readonly-movements">
          <h2 className="font-semibold text-slate-900 mb-3">
            Movimientos ({movements.length})
          </h2>
          {movements.length === 0 ? (
            <p className="text-xs text-slate-500 italic">Sin movimientos.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                    <th className="py-2 pr-3">Fecha</th>
                    <th className="py-2 pr-3">Tipo</th>
                    <th className="py-2 pr-3">Descripción</th>
                    <th className="py-2 pr-3">Categoría</th>
                    <th className="py-2 pr-3 text-right">Monto</th>
                  </tr>
                </thead>
                <tbody>
                  {movements.map((m) => (
                    <tr key={m.id} className="border-b border-slate-100 last:border-0">
                      <td className="py-1.5 pr-3 tabular-nums text-slate-500">{fmtDate(m.date)}</td>
                      <td className="py-1.5 pr-3">
                        <Badge variant="outline" className={`text-[10px] ${
                          m.type === "income" ? "border-emerald-300 text-emerald-700" :
                          m.type === "expense" ? "border-rose-300 text-rose-700" :
                          "border-slate-300"
                        }`}>{m.type}</Badge>
                      </td>
                      <td className="py-1.5 pr-3">{m.description || "—"}</td>
                      <td className="py-1.5 pr-3 text-slate-500">{m.category || "—"}</td>
                      <td className="py-1.5 pr-3 text-right font-mono tabular-nums">
                        {fmtMoney(m.amount, m.currency || ccy)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <p className="text-[11px] text-slate-400 text-center pt-2">
          Powered by Lumfi Cash Pilot · Vista solo-lectura · Sin acceso de edición
        </p>
      </div>
    </div>
  );
}
